import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import api from "@/lib/api";
import { Landmark, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [showPw, setShowPw] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "member" ? "/portal" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If the email exists, a reset link has been sent.");
      setForgot(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      {/* Left institutional panel */}
      <div className="hidden lg:flex flex-col justify-between bg-ink p-12 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <Landmark className="text-brass" strokeWidth={1.5} size={32} />
          <span className="font-serif text-2xl text-paper tracking-tight">KKPKLB</span>
        </div>
        <div>
          <h2 className="font-serif text-4xl text-paper leading-tight">
            Register of Members
          </h2>
          <p className="text-paper/50 mt-4 max-w-md leading-relaxed">
            Koperasi Kumpulan Profesional Kuala Lumpur Berhad — a secure, PDPA-compliant
            digital membership register and share-capital ledger.
          </p>
          <div className="mt-8 flex gap-6 text-paper/40 text-xs uppercase tracking-widest">
            <span>Encrypted</span><span>·</span><span>Audited</span><span>·</span><span>Immutable</span>
          </div>
        </div>
        <div className="text-paper/30 text-xs font-mono">EST. 2011 · KUALA LUMPUR</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Landmark className="text-brass" strokeWidth={1.5} size={26} />
            <span className="font-serif text-xl text-ink">KKPKLB</span>
          </div>
          <h1 className="font-serif text-3xl text-ink">{forgot ? "Reset Password" : "Log Masuk / Sign In"}</h1>
          <p className="text-sm text-slate mt-2 mb-8">
            {forgot ? "Enter your email to receive a reset link." : "Access the membership register."}
          </p>

          {error && (
            <div className="mb-4 bg-brick-bg text-brick border border-brick/20 px-4 py-3 rounded-sm text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={forgot ? submitForgot : submit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate mb-1 uppercase tracking-wider">Emel / Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink"
                placeholder="nama@email.com"
              />
            </div>
            {!forgot && (
              <div>
                <label className="block text-xs font-medium text-slate mb-1 uppercase tracking-wider">Kata Laluan / Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 pr-11 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    data-testid="toggle-password"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-ink transition-colors"
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-ink text-white hover:bg-ink-light py-2.5 rounded-sm transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {forgot ? "Send Reset Link" : "Log Masuk / Sign In"}
            </button>
          </form>

          <button
            onClick={() => { setForgot(!forgot); setError(""); }}
            className="mt-4 text-xs text-slate hover:text-brass transition-colors"
            data-testid="toggle-forgot"
          >
            {forgot ? "← Back to sign in" : "Forgot password?"}
          </button>
        </div>
      </div>
    </div>
  );
}
